import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../components/table";
import type { DataTableOption, DataTableSortDirection } from "../../../../components/table";
import type { MerchantFilters, MerchantListItem } from "../../types/merchant.types";
import { buildMerchantListColumns } from "./merchantListTableColumns";

interface MerchantListViewProps {
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  merchants: MerchantListItem[];
  filters: MerchantFilters;
  updateFilter: React.Dispatch<React.SetStateAction<MerchantFilters>>;
  dateSortDirection: DataTableSortDirection | null;
  onDateSort: (nextDirection: DataTableSortDirection | null) => void;
  categoryOptions: DataTableOption[];
  verificationOptions: DataTableOption[];
  badgeOptions: DataTableOption[];
  onRowOpen: (merchant: MerchantListItem) => void;
  onApprove: (merchant: MerchantListItem) => void;
  onSuspend: (merchant: MerchantListItem) => void;
  onDelete: (merchant: MerchantListItem) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const MerchantListView: React.FC<MerchantListViewProps> = ({
  loading,
  error = null,
  onRetry,
  merchants,
  filters,
  updateFilter,
  dateSortDirection,
  onDateSort,
  categoryOptions,
  verificationOptions,
  badgeOptions,
  onRowOpen,
  onApprove,
  onSuspend,
  onDelete,
  page,
  totalPages,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      buildMerchantListColumns({
        t,
        filters,
        updateFilter,
        categoryOptions,
        verificationOptions,
        badgeOptions,
        dateSortDirection,
        onDateSort,
        onRowOpen,
        onApprove,
        onSuspend,
        onDelete,
      }),
    [
      t,
      filters,
      updateFilter,
      categoryOptions,
      verificationOptions,
      badgeOptions,
      dateSortDirection,
      onDateSort,
      onRowOpen,
      onApprove,
      onSuspend,
      onDelete,
    ],
  );

  return (
    <div className="relative">
      <DataTable
        columns={columns}
        dataSource={merchants}
        rowKey="id"
        loading={loading}
        loadingLabel={t("merchant.loading_merchants")}
        error={error}
        onRetry={onRetry}
        emptyLabel={t("merchant.no_merchants_available")}
        containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
        tableClassName="min-w-max"
        pagination={{
          page,
          totalPages,
          onPageChange,
        }}
        onRowClick={onRowOpen}
      />
    </div>
  );
};

export default MerchantListView;
